import React, {
  useContext, useEffect, useRef, useState,
} from 'react';

import { NavLink } from 'react-router-dom';
import { AppElement, AppFC } from '../../../interfaces';
import { MyOfferBoxProps } from './types';
import {
  ButtonCategory, cryptoCurrencyNames, MarketTypes, OfferStatusTypes,
} from '../../../constants';

import {
  generateDemandedCurrencyBox,
  generateOfferedCurrencyBox, numberWithCommas,
} from '../../../utils';

import { Button, ListItem, Loader } from '../../Atoms';
import {
  CountryBox, CurrencyPriceBox, OfferTypeBox, PaymentMethodBox,
} from '../../Atoms/offerItems';
import { ViewContext, ViewModalActionTypes } from '../../../stores';
import { useCurrency, useOffer } from '../../../stores/hooks';

export const MyOfferBox: AppFC<MyOfferBoxProps> = ({
  offer,
  tradeOfferIds,
  onDeleteClick,
  offerStatus,
  // isDeleting,
}): AppElement => {
  const [isLoading, setIsLoading] = useState(true);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [rate, setRate] = useState<{ rate: string, currency: string }>();

  const { updateViewModal } = useContext(ViewContext);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const isPublished = offerStatus === OfferStatusTypes.Open;
  const isUnpublished = offerStatus === OfferStatusTypes.Unpublished;

  const { unpublishOffer, publishOffer } = useOffer();
  // const navigate = useNavigate();

  const { getCryptocurrencyRate } = useCurrency();
  const {
    id: offerId,
    offerType,
    offeredCurrency,
    demandedCurrency,
    margin,
    paymentMethodTypes,
  } = offer;

  const price = numberWithCommas((Number(rate?.rate) * (100 + margin)) / 100, demandedCurrency, 2);

  const handleDelete = () => {
    updateViewModal({
      type: ViewModalActionTypes.DeleteModal,
      payload: {
        title: 'Delete offer',
        description: 'Are you sure you want to delete this offer?',
        isClosable: false,
        onDelete: () => onDeleteClick(offerId),
      },
    });
  };

  const onUnpublishClick = (id: string) => {
    setIsActionLoading(true);
    unpublishOffer(id)
      .then(() => {
        setIsActionLoading(false);
        window.location.reload();
        updateViewModal({
          type: ViewModalActionTypes.SuccessModal,
          payload: {
            description: 'Offer unpublished successfully!',
          },
        });
      })
      .catch((reason) => {
        updateViewModal({
          type: ViewModalActionTypes.FailModal,
          payload: {
            title: 'Offer unpublish failed.',
            description: reason.detail,
          },
        });
      }).finally(() => {
        setIsActionLoading(false);
      });
  };
  const onPublishClick = (id: string) => {
    setIsActionLoading(true);
    publishOffer(id)
      .then(() => {
        setIsActionLoading(false);
        window.location.reload();
        updateViewModal({
          type: ViewModalActionTypes.SuccessModal,
          payload: {
            description: 'Offer published successfully!',
          },
        });
      })
      .catch((reason) => {
        updateViewModal({
          type: ViewModalActionTypes.FailModal,
          payload: {
            title: 'Offer publish failed.',
            description: reason.detail,
          },
        });
      }).finally(() => {
        setIsActionLoading(false);
      });
  };

  const handleAction = () => {
    updateViewModal({
      type: ViewModalActionTypes.DefaultModal,
      payload: {
        isClosable: false,
        isReload: true,
        content: (
          <div className="flex flex-col gap-14 items-center justify-center">
            <div className="text-center">
              <h4>
                {(isUnpublished && 'Publish') || (isPublished && 'Unpublish')}
                {' '}
                offer
              </h4>
              <p className="mt-2">
                Are you sure you want to
                {' '}
                {(isUnpublished && 'publish') || (isPublished && 'unpublish')}
                {' '}
                this offer?
              </p>
            </div>
            <div className="flex gap-2 w-full [&>button]:w-full max-[400px]:flex-col">
              <Button
                category={ButtonCategory.Default}
                onClick={() => updateViewModal({ type: ViewModalActionTypes.Close })}
              >
                Cancel
              </Button>
              <Button
                className="bg-blue hover:bg-blue/60"
                onClick={() => {
                  (isPublished && onUnpublishClick(offerId))
                    || (isUnpublished && onPublishClick(offerId));
                }}
                isLoading={isActionLoading}
              >
                {(isUnpublished && 'Publish') || (isPublished && 'Unpublish')}
              </Button>
            </div>
          </div>
        ),
      },
    });
  };

  useEffect(() => {
    getCryptocurrencyRate(demandedCurrency, offeredCurrency)
      .then((res) => {
        setRate(res);
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsMenuOpen(false);
      }
    };
    if (isMenuOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMenuOpen]);

  const marketType = (margin > 0 && MarketTypes.Above) as MarketTypes
        || (margin < 0 && MarketTypes.Below) as MarketTypes
        || (margin === 0 && MarketTypes.None) as MarketTypes;

  return offerId ? (
    <ListItem>
      <div className="w-full flex gap-8 items-center justify-between flex-[1.25]">
        <OfferTypeBox
          className="md:flex-1"
          offerType={offerType}
          offeredCurrency={generateOfferedCurrencyBox(offeredCurrency)}
          demandedCurrency={generateDemandedCurrencyBox(demandedCurrency)}
        />
        {!isLoading ? (
          <CurrencyPriceBox
            className="md:flex-1 max-md:items-end"
            price={price}
            margin={Math.abs(margin)}
            marketType={marketType}
            info={`This offer is ${margin === 0 ? 'equal to' : `${margin}% ${marketType}`} ${cryptoCurrencyNames[offeredCurrency]} current market price.`}
          />
        ) : <div className="flex-1"><Loader /></div>}
      </div>
      <div className="w-full flex gap-8 items-center justify-between flex-1 lg:flex-[1.75]">
        <PaymentMethodBox
          className="md:flex-1"
          methods={paymentMethodTypes}
        />
        <CountryBox
          className="md:flex-1 max-md:items-end"
          offer={offer}
        />
      </div>
      <div className="self-end relative" ref={menuRef}>
        {/* 3 vertical dots button */}
        <Button
          className="w-10 h-10 p-0 flex items-center justify-center"
          onClick={() => setIsMenuOpen((s) => !s)}
          aria-haspopup="true"
          aria-expanded={isMenuOpen}
        >
          <span className="sr-only">Open actions menu</span>
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-700">
            <circle cx="12" cy="5" r="1" />
            <circle cx="12" cy="12" r="1" />
            <circle cx="12" cy="19" r="1" />
          </svg>
        </Button>

        {isMenuOpen && (
        <div className="absolute overflow-hidden right-0 mt-2 w-32 bg-card rounded shadow-md z-50 [&_button]:text-sm">
          <button
            type="button"
            onClick={() => handleAction()}
            className="w-full text-left px-4 py-2 hover:bg-default rounded-none"
          >
            {(isUnpublished && 'Publish') || (isPublished && 'Unpublish')}
          </button>
          {!(tradeOfferIds.includes(offerId)) ? (

            <>
              {' '}
              <NavLink to={`/offers/${offerId}`} onClick={() => setIsMenuOpen(false)} className="block px-4 py-2 hover:bg-default rounded-none">Edit</NavLink>
              <button
                type="button"
                onClick={() => {
                  handleDelete();
                  setIsMenuOpen(false);
                }}
                className="w-full text-left px-4 py-2 hover:bg-default text-red-800 rounded-none"
              >
                Delete
              </button>
            </>
          ) : null}
        </div>
        )}
      </div>
    </ListItem>
  ) : null;
};
